import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { User, UserRole } from '../types/user';
import { login as apiLogin, logout as apiLogout } from '../api';
import axios from 'axios';
import config from '../config';
import { jwtDecode } from 'jwt-decode';

interface AuthContextType {
  user: User | null;
  token: string | null;
  role: UserRole | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);

  console.log('AuthProvider rendering, current state:', { user, token, role });

  const logout = useCallback(() => {
    console.log('Logging out');
    apiLogout();
    setUser(null);
    setToken(null);
    setRole(null);
    localStorage.removeItem(config.TOKEN_STORAGE_KEY);
    localStorage.removeItem(config.REFRESH_TOKEN_KEY);
    console.log('Logout successful');
  }, []);

  const isTokenExpired = useCallback((token: string) => {
    try {
      const decodedToken: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    console.log('Attempting to refresh token');
    try {
      const refreshToken = localStorage.getItem(config.REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      const response = await axios.post(`${config.API_BASE_URL}/auth/refresh`, { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem(config.TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem(config.REFRESH_TOKEN_KEY, newRefreshToken);
      setToken(accessToken);
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  }, [logout]);

  const checkTokenExpiration = useCallback(async () => {
    if (token && isTokenExpired(token)) {
      console.log('Token is expired or about to expire, refreshing...');
      try {
        await refreshToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        logout();
      }
    }
  }, [token, isTokenExpired, refreshToken, logout]);

  useEffect(() => {
    console.log('AuthProvider useEffect running');
    const storedToken = localStorage.getItem(config.TOKEN_STORAGE_KEY);
    if (storedToken) {
      try {
        const decodedToken: any = jwtDecode(storedToken);
        console.log('Decoded token:', decodedToken);
        setUser({
          id: decodedToken.sub,
          username: decodedToken.username,
          role: decodedToken.role,
          email: decodedToken.email || '',
          firstName: decodedToken.firstName || '',
          lastName: decodedToken.lastName || '',
          isActive: decodedToken.isActive || true,
          createdAt: decodedToken.createdAt || new Date().toISOString(),
          updatedAt: decodedToken.updatedAt || new Date().toISOString(),
          clientId: decodedToken.clientId, // Include clientId from token
        });
        setToken(storedToken);
        setRole(decodedToken.role as UserRole);
        checkTokenExpiration();
        console.log('User data restored from localStorage');
      } catch (error) {
        console.error('Error parsing stored token:', error);
        logout();
      }
    } else {
      console.log('No stored token found');
    }
  }, [checkTokenExpiration, logout]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (token) {
        checkTokenExpiration();
      }
    }, config.REFRESH_TOKEN_INTERVAL);

    return () => clearInterval(intervalId);
  }, [token, checkTokenExpiration]);

  const login = async (username: string, password: string) => {
    console.log('Attempting login for user:', username);
    try {
      const { accessToken, refreshToken, user: userData } = await apiLogin(username, password);
      setUser(userData);
      setToken(accessToken);
      setRole(userData.role);
      localStorage.setItem(config.TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem(config.REFRESH_TOKEN_KEY, refreshToken);
      console.log('Login successful');
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    role,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
